/**
 * @fileoverview Hook personalizado para gestión de productos API
 * @module useProductsApi
 * @category Hook
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useCallback, useRef } from 'react';
import fileUtils from '../util/fileUtils';
import { handleRequestErrors } from '../services/handleRequestError';

/**
 * Hook personalizado para todas las operaciones de productos API.
 *
 * Abstrae todas las consultas y operaciones relacionadas con productos,
 * proporcionando un estado centralizado y funciones para CRUD completo.
 *
 * @returns {Object} Estado y funciones para gestión de productos
 */
const useProductsApi = () => {
  // Estados
  const [products, setProducts] = useState([]);
  const [selectProducts, setSelectProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);

  const requestIdRef = useRef(0);

  /**
   * Obtiene productos desde la API.
   *
   * @param {Object} params - Parámetros de consulta
   * @param {boolean} canConcat - Si concatenar con productos existentes
   * @returns {Promise<Object>} Respuesta de la API
   */
  const fetchProducts = useCallback(async (params = {}, canConcat = false) => {
    const canConcatProducts = canConcat && params.page > 1;
    const encodedParams = new URLSearchParams(params).toString();

    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/products?${encodedParams}`;

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
          'RETAILER-KEY': ENV.UNIQUE_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (currentRequestId !== requestIdRef.current) return data;

      const newProducts = data.products || [];

      setProducts((prevProducts) => (canConcatProducts ? [...prevProducts, ...newProducts] : newProducts));
      setTotalPages(data.total_pages || 1);
      setCurrentPage(data.current_page || 1);

      return data;
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error('Error fetching products:', err);
        setError(err.message);
      }
      throw err;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Crea un nuevo producto.
   *
   * @param {Object} body - Datos del producto
   * @param {string} showSection - Sección a mostrar
   * @returns {Promise<Object>} Producto creado
   */
  const createProduct = useCallback(async (body, showSection) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/products`;
    const formData = fileUtils.objectToFormData({ product: body });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
          'RETAILER-KEY': ENV.UNIQUE_KEY,
          Accept: 'application/json'
        },
        body: formData
      });

      const data = await handleRequestErrors(response);

      // Agregar el producto a la lista local
      setProducts((prevProducts) => [data.product, ...prevProducts]);

      return { product: data.product, showSection };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualiza un producto existente.
   *
   * @param {string} productWebId - ID web del producto
   * @param {Object} body - Datos actualizados del producto
   * @returns {Promise<Object>} Producto actualizado
   */
  const updateProduct = useCallback(async (productWebId, body) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/products/${productWebId}`;
    const formData = fileUtils.objectToFormData({ product: body });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
          'RETAILER-KEY': ENV.UNIQUE_KEY,
          Accept: 'application/json'
        },
        body: formData
      });

      const data = await handleRequestErrors(response);

      // Actualizar el producto en la lista local
      setProducts((prevProducts) => prevProducts.map((product) => (product.web_id === productWebId ? { ...product, ...data.product } : product)));

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Elimina un producto.
   *
   * @param {string} productWebId - ID web del producto
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  const deleteProduct = useCallback(async (productWebId) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/products/${productWebId}`;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
          'RETAILER-KEY': ENV.UNIQUE_KEY,
          'X-Access-Level': 'read-write'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Remover el producto de la lista local
      setProducts((prevProducts) => prevProducts.filter((product) => product.web_id !== productWebId));

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene productos para selector (formularios).
   *
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise<Object>} Lista de productos para selector
   */
  const fetchSelectProducts = useCallback(async (params = {}) => {
    const encodedParams = new URLSearchParams(params).toString();

    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/select_products?${encodedParams}`;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
          'RETAILER-KEY': ENV.UNIQUE_KEY,
          'X-Access-Level': 'read-write'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectProducts(data.products || []);

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Importa productos desde archivo.
   *
   * @param {Object} body - Datos del archivo de importación
   * @returns {Promise<Object>} Estado de la importación
   */
  const importProducts = useCallback(async (body) => {
    const endpoint = `${ENV.SHOPS_BASE_URL}/api/v1/retailers/import_products`;
    const formData = fileUtils.objectToFormData({ product_import: body });

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {

          'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,

          'RETAILER-KEY': ENV.UNIQUE_KEY,
          Accept: 'application/json, text/plain, */*'
        },
        body: formData
      });

      await handleRequestErrors(response, false);

      return { status: true };
    } catch (err) {
      setError(err.message);
      return { status: false };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Resetea el estado de productos.
   */
  const resetProducts = useCallback(() => {
    requestIdRef.current += 1;
    setProducts([]);
    setSelectProducts([]);
    setError(null);
    setCurrentPage(1);
    setTotalPages(1);
  }, []);

  /**
   * Limpia errores.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    products,
    selectProducts,
    loading,
    totalPages,
    currentPage,
    error,

    // Acciones
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    fetchSelectProducts,
    importProducts,
    resetProducts,
    clearError
  };
};

export default useProductsApi;
